import SubscriptionModel, {
  type Subscription,
  type SubscriptionData,
} from "@Modules/subscriptions/Data/Models/SubscriptionModel"
import Visitable from "@Modules/shared/interfaces/visitable.ts"
import type { Feature, UserStatus } from "@Resources/js/laravel.d.ts"
import TFA_TYPE from "@Helpers/constant/two-factor-authenticator"

/**
 * Raw user subscriptions data from the server
 */
export interface UserSubscriptionsData {
  current?: SubscriptionData[]
  future?: SubscriptionData[]
}

/**
 * Raw user data from the server
 */
export interface UserData {
  id: string | null
  name: string
  email: string | null
  photo_url: string | null
  subscriptions?: UserSubscriptionsData
  has_subscription_history?: boolean
  uses_two_factor_auth?: number
  active_two_factor_backup_codes_count?: number
  facebook_id?: string | null
  google_id?: string | null
  twitter_id?: string | null
  microsoft_id?: string | null
  status?: number | null
}

/**
 * Flag set on a user's status bit field when support keeps their invoices off the
 * billing page.
 */
const HIDE_INVOICES: UserStatus = 0x10

/**
 * User smart object with helper methods
 */
export interface User extends Visitable<User> {
  id: string | null
  name: string
  email: string | null
  photo_url: string | null
  subscription: Subscription
  futureSubscription: Subscription | null
  hasSubscriptionHistory: boolean
  isTwoFactorEnabled: boolean
  hasBackupCodes: boolean
  isLoggedIn: () => boolean
  hasHiddenInvoices: () => boolean
  can: (feature: Feature) => boolean
  hasSocialAccount: (provider: string) => boolean
}

/**
 * Default data for guest users (not logged in)
 */
export const GUEST_USER_DATA: UserData = {
  id: null,
  name: "Guest",
  email: null,
  photo_url: null,
  subscriptions: undefined,
  has_subscription_history: false,
  uses_two_factor_auth: TFA_TYPE.DISABLED,
  active_two_factor_backup_codes_count: 0,
  facebook_id: null,
  google_id: null,
  twitter_id: null,
  microsoft_id: null,
  status: 0,
}

/**
 * Default visit behavior - navigate to user profile settings
 */
const VisitUser = (user: User, query: Record<string, unknown> = {}): void => {
  const theRouter = window.$router

  theRouter.push({
    name: "settings.profile",
    query,
  })
}

/**
 * Creates a User smart object from raw server data.
 *
 * @param data The raw user data from the server (null for guest users)
 */
export default function UserModel(data: UserData | null): User {
  const userData = data ?? GUEST_USER_DATA

  // Get the current default subscription (type === 'default')
  const currentSubscriptionData =
    userData.subscriptions?.current?.find((s) => s.type === "default") ?? null

  // Get the future subscription if scheduled
  const futureSubscriptionData = userData.subscriptions?.future?.[0] ?? null

  // Create Subscription models
  const subscription = SubscriptionModel(currentSubscriptionData)
  const futureSubscription = futureSubscriptionData
    ? SubscriptionModel(futureSubscriptionData)
    : null

  let visitOverride: (model: User, query: Record<string, unknown>) => void = VisitUser

  const that: User = {
    id: userData.id,
    name: userData.name,
    email: userData.email,
    photo_url: userData.photo_url,
    subscription,
    futureSubscription,
    hasSubscriptionHistory: userData.has_subscription_history ?? false,
    isTwoFactorEnabled: (userData.uses_two_factor_auth ?? TFA_TYPE.DISABLED) !== TFA_TYPE.DISABLED,
    hasBackupCodes: (userData.active_two_factor_backup_codes_count ?? 0) > 0,

    /**
     * Returns true if the user is logged in (has an email)
     */
    isLoggedIn(): boolean {
      return userData.email != null
    },

    /**
     * Returns true when the user's invoices are kept off their billing page
     */
    hasHiddenInvoices(): boolean {
      return ((userData.status ?? 0) & HIDE_INVOICES) === HIDE_INVOICES
    },

    /**
     * Returns true if the user's subscription has the given feature
     */
    can(feature: Feature): boolean {
      return subscription.can(feature)
    },

    /**
     * Returns true if the user has linked the given social provider
     */
    hasSocialAccount(provider: string): boolean {
      const key = `${provider}_id` as keyof UserData
      const value = userData[key]
      return typeof value === "string" && value.length > 0
    },

    /**
     * Navigate to the user profile settings page
     */
    visit(query: Record<string, unknown> = {}): void {
      visitOverride(that, query)
    },

    /**
     * Override the default visit behavior
     */
    setVisit(callback: (model: User, query?: Record<string, unknown>) => void): User {
      visitOverride = callback
      return that
    },
  }

  return that
}

/**
 * Creates a guest user object (for non-logged-in users)
 */
export function createGuestUser(): User {
  return UserModel(null)
}
